import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Switch } from '@/components/ui';
import { getNotificationConfig, setNotificationConfig, previewReport, sendReportNow, releaseHeldReport, skipHeldReport, type NotifMetric } from '@/data/client';


type ReportRuleState = Record<NotifMetric, { on: boolean; pct: number; direction: 'drop' | 'either' }>;

// Monthly report config for one client — a section in the client settings modal.
export function MonthlyReportSection({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['notifConfig', orgId], queryFn: () => getNotificationConfig(orgId) });
  const [enabled, setEnabled] = useState(false);
  const [recips, setRecips] = useState<Set<string>>(new Set());
  const [rules, setRules] = useState<ReportRuleState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const inited = useRef(false);

  useEffect(() => {
    if (!data || inited.current) return;
    inited.current = true;
    setEnabled(data.enabled);
    setRecips(new Set(data.recipients));
    const map = {} as ReportRuleState;
    for (const m of data.metrics) map[m.metric] = { on: false, pct: 20, direction: m.metric === 'sessions' || m.metric === 'leads' ? 'drop' : 'either' };
    for (const g of data.guardrails) map[g.metric] = { on: true, pct: g.pct, direction: g.direction };
    setRules(map);
  }, [data]);

  const buildGuardrails = () => (rules ? (Object.entries(rules) as [NotifMetric, ReportRuleState[NotifMetric]][]).filter(([, r]) => r.on).map(([metric, r]) => ({ metric, pct: r.pct, direction: r.direction })) : []);
  const save = async () => {
    setSaving(true); setSaved(false);
    try { await setNotificationConfig(orgId, { enabled, recipients: [...recips], guardrails: buildGuardrails() }); setSaved(true); qc.invalidateQueries({ queryKey: ['notifConfig', orgId] }); }
    finally { setSaving(false); }
  };
  const doPreview = async () => {
    setBusy('preview');
    try { const r = await previewReport(orgId); const w = window.open('', '_blank'); if (w) { w.document.write(r.html); w.document.close(); } }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Preview failed'); } finally { setBusy(''); }
  };
  const run = async (label: string, fn: () => Promise<{ ok: boolean; recipients?: number; error?: string }>, confirmText?: string) => {
    if (confirmText && !confirm(confirmText)) return;
    setBusy(label); setMsg('');
    try { const r = await fn(); setMsg(r.ok ? (r.recipients != null ? `Sent to ${r.recipients} recipient(s).` : 'Done.') : (r.error || 'Failed.')); qc.invalidateQueries({ queryKey: ['notifConfig', orgId] }); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Error'); } finally { setBusy(''); }
  };
  const setRule = (m: NotifMetric, patch: Partial<ReportRuleState[NotifMetric]>) => setRules((r) => (r ? { ...r, [m]: { ...r[m], ...patch } } : r));

  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">Monthly client report (email)</h4>
      <p className="dash-muted admin-sect__note">A monthly KPI email to this client&rsquo;s selected members, sent automatically on the <strong>1st at 8am ET</strong>. Off unless enabled. If a guarded KPI swings past its threshold vs last month, the email is <strong>held</strong> and the Account Manager is asked to review (any admin can release).</p>
      {!data || !rules ? <p className="dash-muted">Loading…</p> : (
        <div className="admin-prof">
          {data.heldAt && (
            <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
              <strong style={{ color: '#b91c1c' }}>Held for review</strong> — {data.heldReason || 'a KPI moved sharply vs last month.'}
              <div className="admin-rowactions" style={{ marginTop: 8 }}>
                <Button variant="primary" size="sm" disabled={busy === 'release'} onClick={() => run('release', () => releaseHeldReport(orgId), 'Send this held report to the selected members now?')}>{busy === 'release' ? 'Sending…' : 'Approve & send'}</Button>
                <Button variant="ghost" size="sm" disabled={busy === 'skip'} onClick={() => run('skip', async () => { await skipHeldReport(orgId); return { ok: true }; }, 'Skip this month (do not send)?')}>Skip this month</Button>
              </div>
            </div>
          )}

          <Switch label="Send the monthly report for this client" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />

          <div>
            <span className="tr-field__label">Recipients (client members)</span>
            {data.members.length === 0 ? (
              <p className="dash-muted admin-sect__note" style={{ marginTop: 4 }}>No client members yet — invite the client&rsquo;s users first.</p>
            ) : (
              <div style={{ display: 'grid', gap: 4, marginTop: 6 }}>
                {data.members.map((m) => (
                  <label key={m.userId} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={recips.has(m.userId)} onChange={(e) => setRecips((prev) => { const n = new Set(prev); if (e.target.checked) n.add(m.userId); else n.delete(m.userId); return n; })} />
                    <span>{m.name} <span className="dash-muted">· {m.email}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <span className="tr-field__label">Guardrails — hold the email if a KPI is off vs last month</span>
            <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
              {data.metrics.map((m) => {
                const r = rules[m.metric];
                return (
                  <div key={m.metric} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 130 }}>
                      <input type="checkbox" checked={r.on} onChange={(e) => setRule(m.metric, { on: e.target.checked })} />
                      <span>{m.label}</span>
                    </label>
                    <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>hold if</span>
                    <input type="number" min={1} max={100} value={r.pct} disabled={!r.on} style={{ width: 60, padding: '3px 6px', border: '1px solid var(--border,#d1d5db)', borderRadius: 6 }} onChange={(e) => setRule(m.metric, { pct: Math.max(1, Math.min(100, Number(e.target.value) || 20)) })} />
                    <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>%</span>
                    <select className="tr-select" value={r.direction} disabled={!r.on} onChange={(e) => setRule(m.metric, { direction: e.target.value as 'drop' | 'either' })}>
                      <option value="drop">lower</option>
                      <option value="either">off either way</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="admin-rowactions" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="primary" size="sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save report settings'}</Button>
            <Button variant="outline" size="sm" disabled={busy === 'preview'} onClick={doPreview}>{busy === 'preview' ? 'Opening…' : 'Preview'}</Button>
            <Button variant="outline" size="sm" disabled={busy === 'send'} onClick={() => run('send', () => sendReportNow(orgId), 'Send the monthly report now to the selected members? (skips guardrails)')}>{busy === 'send' ? 'Sending…' : 'Send now'}</Button>
            {saved && <span className="dash-muted">Saved.</span>}
            {data.lastSent && <span className="dash-muted">Last sent {new Date(data.lastSent.replace(' ', 'T')).toLocaleDateString()}</span>}
            {msg && <span className="dash-muted">{msg}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
